/**
 * lista.ts — numbered list builder for pdfmake.
 *
 * Produces a pdfmake ContentTable with two columns:
 *   - Column 0 (narrow, ~34pt / 12mm): the item number (e.g. "3.1") — navy background,
 *     white centered text, bold.
 *   - Column 1 (*): the item description — white/zebra alternating background, dark ink.
 *
 * Used for §3 Actividades, §4 Problemas, §5 Recomendaciones.
 * The `prefix` argument is the section number (e.g. "3", "4", "5").
 *
 * Layout uses the same fillColor mechanism as buildTable:
 *   - col-0: always theme.primary (navy), regardless of row parity
 *   - col-1: alternating ZEBRA_COLOR on odd rows / transparent (white) on even rows
 *   - Border: 0.5pt #c9d2dd horizontal and vertical lines
 */
import type { ContentTable, Content } from "pdfmake/interfaces.js";
import { autoContrast, subtleBorder, zebraFill } from "./color.js";

const BORDER = subtleBorder("#ffffff");

export interface ListaTheme {
  primary: string;
}

/**
 * Build a numbered list as a pdfmake ContentTable.
 *
 * @param prefix - Section number prefix (e.g. "3" → items numbered "3.1", "3.2" …)
 * @param items  - Array of description strings.
 * @param theme  - Brand theme for the navy number column.
 */
export function buildLista(prefix: string, items: string[], theme: ListaTheme): ContentTable {
  // Number column sits on primary fill → contrast against it; zebra tinted by brand.
  const onPrimary = autoContrast(theme.primary);
  const zebra = zebraFill("#ffffff", theme.primary);
  const body: Content[][] = items.map((desc, i) => [
    {
      text: `${prefix}.${i + 1}`,
      color: onPrimary,
      bold: true,
      fontSize: 9,
      alignment: "center",
    } as Content,
    {
      text: desc,
      color: "#333333",
      fontSize: 9,
    } as Content,
  ]);

  return {
    table: {
      headerRows: 0,
      dontBreakRows: true,
      widths: [34, "*"],
      body,
    },
    layout: {
      fillColor: (rowIndex, _node, columnIndex) => {
        // col-0: always navy (number column — never overridden by zebra)
        if (columnIndex === 0) return theme.primary;
        // col-1: alternating zebra on odd rows, transparent on even rows
        return rowIndex % 2 === 1 ? zebra : null;
      },
      hLineWidth: () => 0.5,
      hLineColor: () => BORDER,
      // All vertical lines at 0.5pt — left outer (i=0), inner separator (i=1), right outer (i=2) (FIX-2).
      vLineWidth: () => 0.5,
      vLineColor: () => BORDER,
      paddingLeft: () => 4,
      paddingRight: () => 4,
      paddingTop: () => 3,
      paddingBottom: () => 3,
    },
    margin: [0, 0, 0, 8],
  } as ContentTable;
}
